<template>
	<div class="formulate-v-calendar">
		<b-card bg-variant="dark" text-variant="grey">
			<b-card-text>
				Select one or more dates or date ranges.
				To adjust a date, simply click it.
				To remove a date, click the trash-can icon.
				For a single day, click the same date twice.
				Click "Add Date" below to get started.
			</b-card-text>

			<b-alert :show="elevenFiftyNineAlert" variant="danger" class="mt-3">
				<strong>Heads up!</strong> Are you sure you selected the right times? You probably want coverage until the end of the day at <strong>11:59 PM</strong>.
				If you are positive you have the correct date and time selected, you may continue.
			</b-alert>

			<b-card-text class="mt-4">
				<b-button
					variant="outline-success"
					@click.p.prevent="addDate">
					<span class="btn-icon mr-2"><font-awesome-icon :icon="['fas', 'plus']" /></span>
					<span class="btn-label">Add Date</span>
				</b-button>
			</b-card-text>

			<hr class="mt-4" />

			<v-date-picker
				:id="context.id"
				v-model="selected.date"
				:mode="mode"
				:min-date="minDate"
				:max-date="maxDate"
				:popover="{ visibility: 'click' }"
				:name="context.name"
				is-range
				is-dark>
				<template #default="{ inputValue, togglePopover, hidePopover }">
					<div v-if="length(dates)" class="card-text">
						<div class="d-flex flex-wrap">
							<b-button-group v-for="(date, i) in dates"
								:key="context.name + '-' + i + '-date'"
								size="lg"
								class="mr-3 mt-2 mb-1">
								<b-button
									ref="button"
									size="lg"
									variant="outline-primary"
									@click.stop="dateSelected($event, date, togglePopover)">
									<span>{{ displayValue(date) }}</span>
								</b-button>
								<b-button variant="outline-primary" @click.stop="removeDate(date, hidePopover)">
									<span class="text-danger"><font-awesome-icon :icon="['fas', 'trash']" /></span>
								</b-button>
							</b-button-group>
						</div>
					</div>
					<div v-else>
						<p class="text-muted"><em>No dates added.</em></p>
					</div>
				</template>
			</v-date-picker>
		</b-card>
	</div>
</template>
<script>
import content from '../../Pages/Backend/Forms/Forms/Content.vue'
import auth from '@/Mixin/auth'

export default {
	mixins: [auth],
	props: {
		context: {
			type: Object,
			required: true
		}
	},
	data () {
		return  {
			mode: 'date',
			auth: {},
			dates: [],
			selected: {}
		}
	},
	computed: {
		content () {
			return content
		},
		minDate () {
			if (this.isStaff) {
				return null
			}

			if (this.context.attributes.min) {
				return this.parseDate(this.context.attributes.min)
			}

			return new Date()
		},
		maxDate () {
			if (this.isStaff) {
				return null
			}

			if (this.context.attributes.max) {
				this.$nextTick(() => {
					return this.parseDate(this.context.attributes.max)
				})
			}

			let maxDate = new Date()
			maxDate.setFullYear(maxDate.getFullYear() + 2)
			maxDate.setDate(maxDate.getDate() + 1)
			maxDate = this.parseDate(maxDate)

			return maxDate
		},
		timePicker () {
			if (this.context && this.context.attributes.hasOwnProperty('time')) {
				return this.context.attributes.time
			}

			return false
		},
		elevenFiftyNineAlert () {
			/**
			 * If the user selects a multi-day event, the last datetime of the event should probably
			 * be 11:59 PM. Users may select 12 PM on accident, or some other weird time. Oops!
			 */
			if(! Array.isArray(this.context.model)) {
				return false
			}

			return this.context.model.some(item => {
				const startDateTime = item.date.start
				const endDateTime = item.date.end
				const startDate = startDateTime.slice(0, 10)
				const endDate = endDateTime.slice(0, 10)
				const startTime = startDateTime.slice(11)
				const endTime = endDateTime.slice(11)

				return !(startDate === endDate && '00-00' === startTime && '00-00' === endTime) && ('00-00' !== endTime && '23-59' !== endTime)
			})
		}
	},
	watch: {
		dates: {
			handler (dates) {
				const currentDates = this.length(this.context.model)
				this.$nextTick(() => this.updateContext())

				if (this.length(dates) > currentDates) {
					setTimeout(() => {
						const btn = this.$refs.button[this.$refs.button.length - 1]
						btn.click()
					}, 500)
				}
			},
			deep: true
		}
	},
	mounted () {
		if (this.$page.props.hasOwnProperty('auth')) {
			this.auth = this.$page.props.auth
		}

		if (! this.length(this.dates) && this.length(this.context.model)) {
			this.setDatesFromContext()
		}

		if (this.timePicker) {
			this.mode = 'dateTime'
		}

		this.$nextTick(() => this.updateContext())
	},
	methods: {
		formatDate (date) {
			if (!date) {
				return
			}

			const d = new Date(date)
			const pad = (num) => (num < 10 ? '0' : '') + num

			const year = d.getFullYear()
			const month = pad(d.getMonth() + 1)
			const day = pad(d.getDate())

			let hour = pad(d.getHours())
			const minute = pad(d.getMinutes())

			if (this.timePicker) {
				return `${year}-${month}-${day}-${hour}-${minute}`
			}

			return `${year}-${month}-${day}`
		},
		setDatesFromContext () {
			let formattedDates = []

			const reformatDateString = (dateStr) => {
				return dateStr.replace(' ', '-').replace(':', '-')
			}

			const processDateObject = (dateObj) => {
				if (! dateObj.hasOwnProperty('start')) return
				if (! dateObj.hasOwnProperty('end')) return

				formattedDates.push({
					date: {
						start: this.parseDate(reformatDateString(dateObj.start)),
						end: this.parseDate(reformatDateString(dateObj.end))
					}
				})
			}

			if (Array.isArray(this.context.model)) {
				for (const dateObj of this.context.model) {
					if (! dateObj.hasOwnProperty('date')) return
					processDateObject(dateObj.date)
				}
			} else {
				processDateObject(this.context.model)
			}

			this.dates = formattedDates
		},
		updateContext () {
			if (! this.length(this.dates)) {
				// eslint-disable-next-line vue/no-mutating-props
				this.context.model = []

				return
			}

			let formattedDates = []
			for (const datesKey in this.dates) {
				if (!this.dates.hasOwnProperty(datesKey)) continue
				const date = this.dates[datesKey].date

				formattedDates.push({
					date: {
						'start': this.formatDate(date.start),
						'end': this.formatDate(date.end)
					}
				})
			}

			this.$set(this.context, 'model', formattedDates)
			this.$emit('change', formattedDates)
		},
		parseDate (dateString) {
			if (typeof dateString !== 'string') {
				return dateString
			}

			// If we pass in the date with dashes here then the timezone takes over
			// we're just replacing the dashes so date sees it as UTC since we don't care about time

			dateString = dateString.split('-')

			let newDate = new Date()
			newDate.setFullYear(parseInt(dateString[0]))
			newDate.setDate(parseInt(dateString[2]))
			newDate.setMonth(parseInt(dateString[1]) - 1)

			if (this.timePicker) {
				newDate.setHours(parseInt(dateString[3]))
				newDate.setMinutes(parseInt(dateString[4]))
				newDate.setSeconds(0)
			} else {
				newDate.setHours(0)
				newDate.setMinutes(0)
				newDate.setSeconds(0)
			}

			return newDate
		},
		renderDate (date) {
			if (! date) {
				return
			}

			const d = new Date(date)
			const pad = (num) => (num < 10 ? '0' : '') + num

			const year = d.getFullYear()
			const month = pad(d.getMonth() + 1)
			const day = pad(d.getDate())

			return `${month}/${day}/${year}`
		},
		renderDateTime (date) {
			if (! date) {
				return
			}

			const d = new Date(date)
			const pad = (num) => (num < 10 ? '0' : '') + num

			const year = d.getFullYear()
			const month = pad(d.getMonth() + 1)
			const day = pad(d.getDate())
			let hour = d.getHours()
			const minute = pad(d.getMinutes())
			const ampm = hour >= 12 ? 'PM' : 'AM'

			hour %= 12
			hour = hour || 12 // Hour '0' should be '12'
			hour = pad(hour)

			return `${month}/${day}/${year} at ${hour}:${minute} ${ampm}`
		},
		displayValue (inputValue) {
			const start = inputValue.date.start.toLocaleString()
			const end = inputValue.date.end.toLocaleString()

			if (start && end && start === end) {
				return this.renderDate(inputValue.date.start)
			}

			if (start && end) {
				if (this.timePicker) {
					return this.renderDateTime(inputValue.date.start) + ' through ' + this.renderDateTime(inputValue.date.end)
				}

				return this.renderDate(inputValue.date.start) + ' through ' + this.renderDate(inputValue.date.end)
			}

			return 'Select a date range...'
		},
		addDate () {
			const midnightDate = () => {
				const date = new Date()
				date.setHours(0, 0, 0, 0)
				return date
			}

			this.dates.push({
				date: {
					start: midnightDate(),
					end: midnightDate()
				}
			})
		},
		removeDate (date, hide) {
			this.dates = this.dates.filter((d) => d !== date)
			hide()
		},
		dateSelected (e, date, toggle) {
			this.selected = date
			toggle({ ref: e.target })
		}
	}
}
</script>
